import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormActions } from "@jbnl/shared-ui";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { NOODZAAK, SCOPE, type Functie } from "./useStamgegevens";

export type FunctieWaarden = {
  name: string;
  scope: number;
  noodzaak: number;
  weergavenaam: string | null;
};

/**
 * Two selects, the fixed vocabulary ADR-0028 allows. Scope locks once a bezetting names the
 * functie, because the server refuses the move; noodzaak does not, since the waarschuwingen it
 * lights up are the beheerder's own statement (ADR-0061). `Field` is flex, so the columns align.
 */
export function FunctieForm({
  id,
  functie,
  submitLabel,
  pending,
  error,
  scopeVast,
  onSubmit,
}: {
  id: string;
  functie?: Functie;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  scopeVast?: boolean;
  onSubmit: (waarden: FunctieWaarden) => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const veld = useId();
  const [name, setName] = useState(functie?.name ?? "");
  const [scope, setScope] = useState<number>(
    functie?.scope ?? SCOPE.wedstrijdbaan,
  );
  const [noodzaak, setNoodzaak] = useState<number>(
    functie?.noodzaak ?? NOODZAAK.verplicht,
  );
  const [weergavenaam, setWeergavenaam] = useState(functie?.weergavenaam ?? "");

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name,
          scope,
          noodzaak,
          weergavenaam: weergavenaam.trim() || null,
        });
      }}
    >
      <Field>
        <FieldLabel htmlFor={`${id}-${veld}-naam`}>
          {t("functies.naam")}
        </FieldLabel>
        <Input
          id={`${id}-${veld}-naam`}
          value={name}
          maxLength={120}
          required
          placeholder={t("functies.naamPlaceholder")}
          onChange={(event) => setName(event.target.value)}
        />
        <FieldDescription>{t("functies.naamUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor={`${id}-${veld}-weergavenaam`}>
          {t("functies.weergavenaam")}
        </FieldLabel>
        <Input
          id={`${id}-${veld}-weergavenaam`}
          value={weergavenaam}
          maxLength={120}
          placeholder={t("functies.weergavenaamPlaceholder")}
          onChange={(event) => setWeergavenaam(event.target.value)}
        />
        <FieldDescription>{t("functies.weergavenaamUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor={`${id}-${veld}-scope`}>
          {t("functies.dient")}
        </FieldLabel>
        <Select
          id={`${id}-${veld}-scope`}
          value={String(scope)}
          disabled={scopeVast}
          onChange={(event) => setScope(Number(event.target.value))}
        >
          <option value={String(SCOPE.wedstrijdbaan)}>
            {t("functies.dientBaan")}
          </option>
          <option value={String(SCOPE.wedstrijddag)}>
            {t("functies.dientDag")}
          </option>
        </Select>
        {scopeVast && (
          <FieldDescription>{t("functies.scopeVast")}</FieldDescription>
        )}
      </Field>

      <Field>
        <FieldLabel htmlFor={`${id}-${veld}-noodzaak`}>
          {t("functies.moetBezetZijn")}
        </FieldLabel>
        <Select
          id={`${id}-${veld}-noodzaak`}
          value={String(noodzaak)}
          onChange={(event) => setNoodzaak(Number(event.target.value))}
        >
          <option value={String(NOODZAAK.verplicht)}>
            {t("noodzaak.verplicht")}
          </option>
          <option value={String(NOODZAAK.optioneel)}>
            {t("noodzaak.optioneel")}
          </option>
        </Select>
        <FieldDescription>{t("functies.moetBezetZijnUitleg")}</FieldDescription>
      </Field>

      <FormActions>
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button type="submit" disabled={pending}>
          {pending ? t("common:actions.busy") : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}
